import { ApolloClient, ApolloLink, HttpLink, Observable } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import {
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
  LocalStateError,
  ServerError,
  ServerParseError,
  UnconventionalError,
} from '@apollo/client/errors';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import * as Sentry from '@sentry/browser';
import { get, isObject } from 'lodash';
import { map } from 'rxjs';
import { messageContext } from './common/AppContextHolder';
import { ROUTES, TOKEN } from './common/constants';
import {
  getIsRefreshing,
  getPendingRefreshPromise,
  isRefreshableAuthError,
  isTokenExpired,
  refreshTokens,
  setOnRefreshFailure,
  startSessionExpiryWatch,
} from './common/tokenManager';
import history from './historyData';

// Perform a single logout redirect when the refresh token is permanently rejected,
// instead of every failed request/interceptor redirecting on its own.
setOnRefreshFailure(() => {
  // eslint-disable-next-line no-undef
  const token = localStorage.getItem(TOKEN);
  if (token) {
    history.replace(ROUTES?.LOGOUT, {
      // eslint-disable-next-line no-undef
      pathname: window?.location?.pathname,
    });
  } else {
    // eslint-disable-next-line no-undef
    localStorage.clear();
  }
});

// Proactively end the session when the refresh token expires, even if the user is idle
// (no request to trigger a refresh). Checks on an interval and logs out at ~token expiry.
startSessionExpiryWatch();

let disableToastTimeout = null;
export const cacheData = new InMemoryCache();

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_SERVER_URL,
});

const toast = ({ message: content, type }) => {
  messageContext?.destroy();
  switch (type) {
    case 'info':
      messageContext?.info(content);
      break;
    case 'success':
      messageContext?.success(content);
      break;
    case 'warning':
      messageContext?.warning(content);
      break;
    case 'error':
      messageContext?.error(content);
      break;
    default:
      break;
  }
};

const authLink = new SetContextLink(async ({ headers } = {}) => {
  // If a token refresh is in progress, wait for it to complete before sending the request
  if (getIsRefreshing() && getPendingRefreshPromise()) {
    await getPendingRefreshPromise().catch(() => {});
  }

  // eslint-disable-next-line no-undef
  let userToken = localStorage.getItem(TOKEN);

  // Proactively refresh if token is expired before sending any request
  if (userToken && isTokenExpired(userToken)) {
    try {
      userToken = await refreshTokens();
    } catch {
      // Refresh failed, continue with expired token (errorLink will handle it)
    }
  }

  let newHeaders = headers || {};

  newHeaders = {
    ...newHeaders,
    Authorization: userToken ? `Bearer ${userToken}` : '',
  };

  return {
    headers: newHeaders,
  };
});

const responseMessageLink = new ApolloLink((operation, forward) =>
  forward(operation).pipe(
    map((response) => {
      const { data } = response;

      if (
        data &&
        isObject(data) &&
        Object?.keys(data)?.length > 0 &&
        data?.[`${Object?.keys(data)?.[0]}`]?.message
      ) {
        if (Object?.keys(data)?.[0] === 'forgotUserPassword') {
          if (data?.[`${Object?.keys(data)?.[0]}`]?.status !== 'ERROR') {
            setTimeout(() => {
              toast({
                message:
                  data?.[`${Object?.keys(data)?.[0]}`]?.message ||
                  'Operation successful',
                type: 'success',
              });
            }, 1000);
          }
        } else {
          setTimeout(() => {
            const oResponse = data?.[`${Object?.keys(data)?.[0]}`];

            if (!response) {
              return;
            }

            toast({
              message: oResponse.message || 'Operation successful',
              type: oResponse.status === 'ERROR' ? 'error' : 'success',
            });
          }, 1000);
        }
      }
      return response;
    }),
  ),
);

// Helper function to log error to console and Sentry
const logError = (errorType, error) => {
  const message = error?.message?.toString();
  // eslint-disable-next-line no-console
  console.log(`[${errorType} error]: ${message}`);
  Sentry?.captureException(new Error(`[${errorType} error]: ${message}`));
};

// Helper function to handle 405 status errors
const handle405Error = (errorMessage) => {
  if (disableToastTimeout) {
    clearTimeout(disableToastTimeout);
  }

  disableToastTimeout = setTimeout(() => {
    if (errorMessage) {
      toast({
        message: errorMessage,
        type: 'error',
      });
    }
  }, 200);

  history?.replace(ROUTES?.LOGOUT);
};

// Helper function to handle GraphQL error extensions
// eslint-disable-next-line no-unused-vars
const handleGraphQLErrorExtensions = (extensions, errorMessage) => {
  if (extensions?.code === 'FORBIDDEN') {
    history.replace('/access-denied');
    return;
  }

  if (
    extensions?.code === 'UNAUTHENTICATED' ||
    extensions?.exception?.code === 405 ||
    extensions?.exception?.name === 'JsonWebTokenError'
  ) {
    history.replace(ROUTES?.LOGOUT);
  }
};

// Helper function to handle GraphQL errors
const handleGraphQLErrors = (error, result) => {
  const graphQLErrors = error?.errors;

  if (graphQLErrors?.length > 0) {
    const isForBidden =
      get(graphQLErrors?.[0], 'extensions.code') === 'FORBIDDEN';

    if (!isForBidden) {
      setTimeout(() => {
        toast({
          message: graphQLErrors?.[0]?.message,
          type: 'error',
        });
      }, 1000);
    }
  }

  if (result?.errors && Array.isArray(result?.errors)) {
    result?.errors?.forEach((err) => {
      const { message: errorMessage, locations, path, extensions } = err;

      // Enable when sentry integrated
      Sentry.captureException(
        new Error(
          `[Response error]: Message: ${errorMessage}, Location: ${locations}, Path: ${path}`,
        ),
      );

      handleGraphQLErrorExtensions(extensions, errorMessage);

      // eslint-disable-next-line no-console
      return console.log(
        `[Response error]: Message: ${errorMessage}, Location: ${locations}, Path: ${path}`,
      );
    });
  }
};

// Helper function to handle server errors
const handleServerError = (serverError) => {
  if (serverError?.statusCode === 405 || serverError?.status === 405) {
    let errorMessage = serverError?.message;
    try {
      if (serverError?.result?.message) {
        errorMessage = serverError?.result?.message;
      } else if (serverError?.bodyText) {
        const parsed = JSON.parse(serverError?.bodyText);
        errorMessage = parsed?.message || errorMessage;
      }
    } catch (error) {
      // If parsing fails, use the error message
    }
    handle405Error(errorMessage);
    return;
  }

  logError('Server', serverError);
};

// Helper function to handle fallback errors
const handleFallbackError = (error) => {
  if (error?.statusCode === 405 || error?.status === 405) {
    const errorMessage =
      error?.result?.message || error?.bodyText || error?.message;
    handle405Error(errorMessage);
    return;
  }

  logError('Network', error);
};

const errorLink = new ErrorLink((options) => {
  const { error, result, operation, forward } = options;

  if (CombinedGraphQLErrors.is(error)) {
    // Check if any error is a refreshable UNAUTHENTICATED error
    const hasRefreshableAuthError = error?.errors?.some((err) => {
      const { message: errorMessage, extensions } = err;
      return isRefreshableAuthError(extensions, errorMessage);
    });

    if (hasRefreshableAuthError) {
      // eslint-disable-next-line no-undef
      const existingToken = localStorage.getItem(TOKEN);

      // Only attempt refresh if user was previously authenticated
      if (existingToken) {
        // Return Observable to intercept the error, refresh token, and retry
        return new Observable((observer) => {
          refreshTokens()
            .then((newToken) => {
              // Update operation headers with the new token
              const oldHeaders = operation.getContext()?.headers || {};
              operation.setContext({
                headers: {
                  ...oldHeaders,
                  Authorization: `Bearer ${newToken}`,
                },
              });

              // Retry the failed operation
              forward(operation).subscribe({
                next: (retryResult) => observer.next(retryResult),
                error: (retryError) => observer.error(retryError),
                complete: () => observer.complete(),
              });
            })
            .catch(() => {
              // Refresh failed - redirect to logout
              history.replace(ROUTES?.LOGOUT, {
                // eslint-disable-next-line no-undef
                pathname: window?.location?.pathname,
              });
              observer.error(error);
            });
        });
      }
    }

    // Non-refreshable errors fall through to existing handling
    handleGraphQLErrors(error, result);
  } else if (CombinedProtocolErrors.is(error)) {
    logError('Protocol', error);
  } else if (LocalStateError.is(error)) {
    logError('LocalState', error);
  } else if (ServerError.is(error)) {
    handleServerError(error);
  } else if (ServerParseError.is(error)) {
    logError('Parse', error);
    setTimeout(() => {
      toast({
        message: 'Failed to parse server response',
        type: 'error',
      });
    }, 1000);
  } else if (UnconventionalError.is(error)) {
    logError('Unconventional', error);
  } else if (error) {
    handleFallbackError(error);
  } else {
    setTimeout(() => {
      toast({
        message: 'Something went wrong!',
        type: 'error',
      });
    }, 1000);
  }
});

const client = new ApolloClient({
  cache: cacheData,
  link: ApolloLink.from([responseMessageLink, errorLink, authLink, httpLink]),
});

export default client;
